import React from "react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  subtitle?: string;
  className?: string;
  variant?: "default" | "compact" | "rich";
}

/**
 * Reusable empty state component for consultation cards.
 * Design system compliant — uses z-* typography and semantic color tokens.
 *
 * - default: standard padded empty state with dashed border
 * - compact: minimal padding, no border — for use inside card content areas
 * - rich: prominent dashed border, icon container, most spacious
 */
export const EmptyState = ({
  icon,
  title,
  subtitle,
  variant = "default",
  className,
}: EmptyStateProps) => {
  const isCompact = variant === "compact";
  const isRich = variant === "rich";

  const containerClasses = cn(
    "flex flex-col items-center justify-center text-center rounded-xl transition-all",
    isRich
      ? "py-12 px-6 border-2 border-dashed border-border/60 bg-muted/30"
      : "bg-muted/50 border border-dashed border-border/60",
    isCompact ? "py-6 px-4" : !isRich && "py-8 px-6",
    className
  );

  return (
    <div className={containerClasses}>
      {icon && (
        <div
          className={cn(
            "flex items-center justify-center rounded-full mb-3",
            isRich
              ? "h-12 w-12 bg-muted text-muted-foreground mb-4"
              : "h-10 w-10 bg-muted text-muted-foreground mb-2"
          )}
        >
          {React.isValidElement(icon)
            ? React.cloneElement(
                icon as React.ReactElement<{ className?: string }>,
                {
                  className: cn(
                    (icon as React.ReactElement<{ className?: string }>).props
                      .className,
                    isRich ? "h-5 w-5" : "h-4 w-4"
                  ),
                }
              )
            : icon}
        </div>
      )}

      <p
        className={cn(
          isCompact
            ? "z-secondary text-muted-foreground"
            : "z-body-md text-foreground"
        )}
      >
        {title}
      </p>

      {subtitle && (
        <p
          className={cn(
            "mt-1 max-w-sm mx-auto",
            isCompact
              ? "z-caption text-muted-foreground"
              : "z-secondary text-muted-foreground"
          )}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
};
